import React, { useState, useEffect } from 'react';
import { FileText, Download, Calendar } from 'lucide-react';
import { dewarService } from '../services/dewarServiceAppwrite';
import {
  exportDewarsToExcel,
  exportOccupancyReport,
  exportNitrogenLevelReport
} from '../utils/excelExport';

const Reports = () => {
  const [dewars, setDewars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dateRanges, setDateRanges] = useState({
    'Activity Log': { startDate: '', endDate: '' },
    'Liquid nitrogen level report': { startDate: '', endDate: '' }
  });

  useEffect(() => {
    loadDewars();
  }, []);

  const loadDewars = async () => {
    try {
      setLoading(true);
      const data = await dewarService.getAll();
      setDewars(data);
    } catch (err) {
      console.error('Error loading dewars:', err);
      setError('Failed to load dewar data');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (reportType, field, value) => {
    setDateRanges(prev => ({
      ...prev,
      [reportType]: {
        ...prev[reportType],
        [field]: value
      }
    }));
  };

  const handleGenerateReport = async (reportType) => {
    try {
      setLoading(true);
      setError('');

      // Fetch fresh data
      const data = await dewarService.getAll();

      if (!data || data.length === 0) {
        setError('No dewar data available to export');
        return;
      }

      // Get date range for reports that support it
      const dateRange = dateRanges[reportType];

      switch (reportType) {
        case 'Inventory Report':
          // Full inventory with dewars summary, all samples, and nitrogen history
          exportDewarsToExcel(data, 'inventory_report');
          break;
        case 'Occupancy Report':
          // Focus on capacity and utilization
          exportOccupancyReport(data, 'occupancy_report');
          break;
        case 'Activity Log':
          // Same as inventory for now (can be customized later for sample movement)
          exportDewarsToExcel(data, 'activity_log', dateRange);
          break;
        case 'Liquid nitrogen level report':
          // Focus on nitrogen levels and history
          exportNitrogenLevelReport(data, 'nitrogen_level_report', dateRange);
          break;
        default:
          exportDewarsToExcel(data, 'report');
      }
    } catch (err) {
      console.error('Error generating report:', err);
      setError(`Failed to generate ${reportType}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const reportTypes = [
    {
      name: 'Inventory Report',
      description: 'Complete inventory with dewars, samples, and nitrogen history',
      icon: '📊'
    },
    {
      name: 'Occupancy Report',
      description: 'Capacity utilization and availability analysis',
      icon: '📈'
    },
    {
      name: 'Activity Log',
      description: 'Complete sample and dewar activity history',
      icon: '📝'
    },
    {
      name: 'Liquid nitrogen level report',
      description: 'Current levels and historical monitoring data',
      icon: '🌡️'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-6 lg:py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Reports</h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600">Generate and download laboratory reports</p>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {reportTypes?.map((report, index) => {
            const needsDateRange = report?.name === 'Activity Log' || report?.name === 'Liquid nitrogen level report';
            const currentDateRange = dateRanges[report?.name];

            return (
              <div key={index} className="bg-white rounded-lg shadow-md p-4 sm:p-6 flex flex-col">
                <div className="flex items-start mb-4">
                  <span className="text-3xl sm:text-4xl mr-3 sm:mr-4">{report?.icon}</span>
                  <div className="flex-1">
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1 sm:mb-2">{report?.name}</h3>
                    <p className="text-xs sm:text-sm text-gray-600">{report?.description}</p>
                  </div>
                </div>

                {needsDateRange && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Calendar className="w-4 h-4 text-indigo-600" />
                      <span className="text-xs font-semibold text-gray-700">Filter by Date Range (Optional)</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label htmlFor={`start-${index}`} className="text-xs font-medium text-gray-700 block mb-1.5">
                          Start Date
                        </label>
                        <input
                          id={`start-${index}`}
                          type="date"
                          value={currentDateRange?.startDate || ''}
                          onChange={(e) => handleDateChange(report?.name, 'startDate', e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                        />
                      </div>
                      <div>
                        <label htmlFor={`end-${index}`} className="text-xs font-medium text-gray-700 block mb-1.5">
                          End Date
                        </label>
                        <input
                          id={`end-${index}`}
                          type="date"
                          value={currentDateRange?.endDate || ''}
                          onChange={(e) => handleDateChange(report?.name, 'endDate', e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => handleGenerateReport(report?.name)}
                  disabled={loading}
                  className="mt-auto w-full flex items-center justify-center px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm sm:text-base disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Generate Report
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">Report Information</h2>
          <div className="space-y-4">
            <div className="flex items-start p-4 bg-blue-50 rounded-lg">
              <FileText className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900 mb-1">Excel Format</p>
                <p className="text-xs text-gray-600">
                  All reports are generated in Excel (.xlsx) format with multiple sheets for comprehensive data analysis.
                </p>
              </div>
            </div>
            <div className="flex items-start p-4 bg-green-50 rounded-lg">
              <Download className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900 mb-1">Report Types</p>
                <ul className="text-xs text-gray-600 list-disc list-inside space-y-1">
                  <li><strong>Inventory Report:</strong> Dewars summary, all samples, nitrogen history</li>
                  <li><strong>Occupancy Report:</strong> Utilization percentages, capacity analysis, box details</li>
                  <li><strong>Activity Log:</strong> Complete activity history for all dewars and samples</li>
                  <li><strong>Nitrogen Level Report:</strong> Current levels with status, full history with trends</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;